<template>
  <!-- 绩效管理：员工月度报表 + 目标统计（按所选员工过滤） -->
  <div class="report-list">
    <list-page>
      <search-condition @search="loadReport" :show-search="true">
        <template #default>
          <el-select v-model="query.employeeId" placeholder="请选择员工" filterable @change="loadReport">
            <template #prefix>员工</template>
            <el-option v-for="e in staffOptions" :key="e.id" :label="staffLabel(e)" :value="e.employeeId"/>
          </el-select>
          <el-date-picker v-model="query.month" type="month" value-format="YYYY-MM" placeholder="选择月份" @change="loadReport" style="width: 160px"/>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="loadReport">查询</page-button>
        </template>
      </search-condition>
      <list-table>
        <template v-if="report">
          <!-- KPI 作为 list-page 的直接子元素，不放进表格容器 -->
          <div  class="stat-grid">
            <div class="stat-card">
              <div class="label">🎯 实得分合计</div>
              <div class="value" style="color:#67c23a">{{ report.totalActual }}</div>
              <div class="sub">满分 {{ report.totalCap }}（封顶后实得）</div>
            </div>
            <div class="stat-card">
              <div class="label">📉 理论分合计</div>
              <div class="value" style="color:#f56c6c">{{ report.totalTheo }}</div>
              <div class="sub">可扣减为负，反映问题严重度</div>
            </div>
            <div class="stat-card">
              <div class="label">🔻 扣分合计</div>
              <div class="value" style="color:#e6a23c">-{{ report.totalDeductionSum }}</div>
              <div class="sub">{{ (report.deductionList || []).length }} 次扣分</div>
            </div>
            <div class="stat-card">
              <div class="label">✅ 目标完成</div>
              <div class="value">{{ goalSummary.done }}<span class="unit">/{{ goalSummary.total }}</span></div>
              <div class="sub">{{ goalSummary.rate }}% 完成率（不含工作日志）</div>
            </div>
          </div>
          <!-- 考核项明细 -->
          <div class="chart-card" >
            <div class="section-title">考核项明细<span class="section-sub">{{ query.month }}</span></div>
            <yq-table :data="report.itemRows || []" height="auto" :empty-text="'暂无数据'">
              <yq-table-column label="考核项" prop="itemName" min-width="180rem">
                <template v-slot="{row}"><span class="cell-title">{{ row.itemName }}</span></template>
              </yq-table-column>
              <yq-table-column label="说明" prop="desc" min-width="100rem"/>
              <yq-table-column label="扣分合计" prop="deductionSum" min-width="90rem" align="right">
                <template v-slot="{row}">
                  <span class="num" :class="row.deductionSum > 0 ? 'neg' : 'muted'">{{ row.deductionSum > 0 ? '-' + row.deductionSum : '0' }}</span>
                </template>
              </yq-table-column>
              <yq-table-column label="上限" prop="cap" width="80rem" align="center"/>
              <yq-table-column label="实得分" prop="actual" width="90rem" align="right">
                <template v-slot="{row}"><span class="num bold" style="color:#67c23a">{{ row.actual }}</span></template>
              </yq-table-column>
              <yq-table-column label="理论分" prop="theo" width="90rem" align="right">
                <template v-slot="{row}"><span class="num" :style="{color: row.theo < 0 ? '#f56c6c' : '#606266'}">{{ row.theo }}</span></template>
              </yq-table-column>
              <yq-table-column label="状态" prop="overCap" width="120rem" align="center">
                <template v-slot="{row}">
                  <el-tag v-if="row.overCap" size="small" type="danger">超上限封顶</el-tag>
                  <el-tag v-else-if="row.deductionSum > 0" size="small" type="warning">已扣分</el-tag>
                  <el-tag v-else size="small" type="info">无扣分</el-tag>
                </template>
              </yq-table-column>
            </yq-table>
          </div>

          <!-- 本月扣分明细 -->
          <div  class="chart-card">
            <div class="section-title">
              本月扣分明细
              <span class="section-sub">{{ (report.deductionList || []).length }} 条</span>
            </div>
            <yq-table :data="report.deductionList || []" height="auto" :empty-text="'本月暂无扣分'">
              <yq-table-column label="日期" prop="deductionDate" min-width="110rem">
                <template v-slot="{row}">{{ dateOnly(row.deductionDate) }}</template>
              </yq-table-column>
              <yq-table-column label="考核项" prop="itemName" min-width="160rem"/>
              <yq-table-column label="扣分" prop="points" width="90rem" align="right">
                <template v-slot="{row}"><span class="num neg">-{{ row.points }}</span></template>
              </yq-table-column>
              <yq-table-column label="原因" prop="reason" min-width="200rem" :show-overflow-tooltip="true"/>
            </yq-table>
          </div>

          <!-- 目标统计（按所选员工） -->
          <div  class="stat-grid">
            <div class="stat-card">
              <div class="label">📌 目标总数</div>
              <div class="value">{{ goalSummary.total }}</div>
              <div class="sub">{{ query.month }} · {{ currentStaffName }} 合计</div>
            </div>
            <div class="stat-card">
              <div class="label">✅ 已完成</div>
              <div class="value" style="color:#67c23a">{{ goalSummary.done }}</div>
              <div class="sub">完成率 <b>{{ goalSummary.rate }}%</b></div>
            </div>
            <div class="stat-card">
              <div class="label">⏳ 未完成</div>
              <div class="value" style="color:#f56c6c">{{ goalSummary.unfinished }}</div>
              <div class="sub">待办 + 已延期</div>
            </div>
            <div class="stat-card">
              <div class="label">↗ 已延期</div>
              <div class="value" style="color:#B45309">{{ goalSummary.overdue }}</div>
              <div class="sub">标记「未完成」的目标行</div>
            </div>
          </div>
          <!-- 职员完成情况：按 kpi任务 聚合 -->
          <div class="chart-card">
            <div class="section-title">📋 职员完成情况<span class="section-sub">{{ currentStaffName }}</span></div>
            <yq-table :data="taskGroups" height="auto" :empty-text="'该员工本月暂无目标'">
              <yq-table-column label="KPI任务" prop="taskTitle" min-width="220rem" :show-overflow-tooltip="true">
                <template v-slot="{row}"><span class="cell-title">{{ row.taskTitle }}</span></template>
              </yq-table-column>
              <yq-table-column label="总目标" prop="total" width="90rem" align="center"/>
              <yq-table-column label="已完成" prop="done" width="90rem" align="center">
                <template v-slot="{row}"><b class="num" style="color:#67c23a">{{ row.done }}</b></template>
              </yq-table-column>
              <yq-table-column label="未完成" prop="pending" width="90rem" align="center">
                <template v-slot="{row}">
                  <b v-if="row.pending" class="clickable-num num neg" @click="flashUnfinished(row, 0)">{{ row.pending }}</b>
                  <span v-else class="num neg">0</span>
                </template>
              </yq-table-column>
              <yq-table-column label="已延期" prop="overdue" width="90rem" align="center">
                <template v-slot="{row}">
                  <b v-if="row.overdue" class="clickable-num num" style="color:#B45309" @click="flashUnfinished(row, 50)">{{ row.overdue }}</b>
                  <span v-else class="num" style="color:#B45309">0</span>
                </template>
              </yq-table-column>
              <yq-table-column label="完成率" prop="rate" width="90rem" align="center">
                <template v-slot="{row}"><b class="num" :style="{color: rateColor(row.rate)}">{{ row.rate }}%</b></template>
              </yq-table-column>
            </yq-table>
          </div>

          <!-- 职员未完成情况明细（按所选员工）
               该表保留 el-table：点击「未完成 / 已延期」数字后需要 :row-class-name 给命中行
               加 .goal-flash-row 闪烁类，yq-table 未透出该属性。 -->
          <div class="chart-card">
            <div class="section-title">⚠ 职员未完成情况明细<span class="section-sub">{{ currentStaffName }}</span></div>
            <div v-if="!unfinishedRows.length" class="empty">该员工本月没有未完成目标</div>
            <el-table ref="unfinishTable" :data="unfinishedRows" border size="small"
                      :row-class-name="rowClass" v-else>
              <el-table-column label="日期" width="110"><template v-slot="{row}">{{ dateOnly(row.goalDate) }}</template></el-table-column>
              <el-table-column label="状态" width="120" align="center">
                <template v-slot="{row}">
                  <el-tag v-if="row.kpiStatus === 50" size="mini" type="danger">↗ 已延期</el-tag>
                  <el-tag v-else-if="row.delayType === 1" size="mini" type="danger">↗ 任务已延期</el-tag>
                  <el-tag v-else-if="row.remitType === 1" size="mini" type="warning">⏩ 被顺延</el-tag>
                  <el-tag v-else size="mini" type="warning">📋 待办</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="目标内容" min-width="200">
                <template v-slot="{row}">
                  <b>{{ row.title }}</b>
                  <div v-if="row.result" class="muted">{{ row.result }}</div>
                </template>
              </el-table-column>
              <el-table-column label="所属任务" prop="taskTitle" min-width="160">
                <template v-slot="{row}">{{ row.taskTitle || '—' }}</template>
              </el-table-column>
              <el-table-column label="备注" prop="remark" min-width="160">
                <template v-slot="{row}">{{ row.remark || '—' }}</template>
              </el-table-column>
            </el-table>
          </div>
        </template>
        <template v-else>
          <div class="no-data">暂无数据</div>
        </template>
      </list-table>
    </list-page>
  </div>
</template>

<script>
import ListPage from "@/components/h5/layout/ListPage.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import FormPage from "@/components/h5/layout/FormPage.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import {useRoute, useRouter} from "vue-router";
import Util from "@/util/util";
import reportApi from "@/api/dz-crm/kpi/reportApi";
import staffEmployeeApi from "@/api/dz-crm/sys/staffEmployeeApi";
import commonApi from "@/api/commonApi";
import ListTable from "@/components/h5/layout/ListTable.vue";
import YqTable from "@/components/h5/table/YqTable.vue";

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "ReportList",
  components: {
    YqTable,
    ListTable,
    PageButton, SearchCondition, FormPage, ListPage
  },
  data() {
    const now = new Date();
    return {
      loading: false,
      query: {employeeId: "", month: now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0")},
      staffOptions: [],
      kpiTypeDictList: [],
      report: null,
      goalRows: [], // 所选员工当月目标明细（非工作日志的 kpi日历行）
      flashIds: [], // 待高亮闪烁的目标行 id
      flashTimer: null,
    };
  },
  computed: {
    currentStaff() {
      return this.staffOptions.find(e => e.employeeId === this.query.employeeId) || null;
    },
    currentStaffName() {
      const s = this.currentStaff;
      return s ? (s.employeeName || s.employeeId) : "—";
    },
    // 所选员工目标行 → 汇总卡
    goalSummary() {
      const groups = this.taskGroups;
      const total = groups.reduce((s, g) => s + g.total, 0);
      const done = groups.reduce((s, g) => s + g.done, 0);
      const overdue = groups.reduce((s, g) => s + g.overdue, 0);
      return {
        total,
        done,
        unfinished: groups.reduce((s, g) => s + g.unfinished.length, 0),
        overdue,
        rate: total ? Math.round(done / total * 100) : 0,
      };
    },
    // 按 kpi任务 聚合
    taskGroups() {
      const rows = this.goalRows || [];
      const byTask = {};
      rows.forEach(g => {
        const key = (g.taskId && g.taskId !== "0") ? g.taskId : "__none__";
        if (!byTask[key]) {
          byTask[key] = {taskId: key, taskTitle: g.taskTitle || g.title || (key === "__none__" ? "未关联任务" : "未知任务"), list: []};
        }
        byTask[key].list.push(g);
      });
      return Object.keys(byTask).map(key => {
        const t = byTask[key];
        const list = t.list;
        const done = list.filter(x => x.kpiStatus === 100).length;
        const pending = list.filter(x => x.kpiStatus !== 100 && x.kpiStatus !== 50).length;
        const overdue = list.filter(x => x.kpiStatus === 50).length;
        return {
          taskId: t.taskId,
          taskTitle: t.taskTitle,
          list,
          total: list.length,
          done,
          pending,
          overdue,
          unfinished: list.filter(x => x.kpiStatus !== 100),
          rate: list.length ? Math.round(done / list.length * 100) : 0,
        };
      });
    },
    // 未完成明细行（标注所属任务名）
    unfinishedRows() {
      const rows = this.goalRows || [];
      const titleOf = {};
      this.taskGroups.forEach(g => {
        g.list.forEach(x => {
          titleOf[x.id] = g.taskTitle;
        });
      });
      return rows.filter(x => x.kpiStatus !== 100).map(x => ({...x, taskTitle: titleOf[x.id] || x.title}));
    },
  },
  beforeRouteLeave(to, from, next) {
    // 将当前页面数据进行缓存
    Util.cachePageData(this, to, from);
    next();
  },
  created() {
    this.initDict();
    this.initStaff();
  },
  methods: {
    async initDict() {
      const res = await commonApi.queryUniqueDictionaryList("POSITION_ROLE");
      this.kpiTypeDictList = res || [];
    },
    async initStaff() {
      const {records} = await staffEmployeeApi.queryList({pageNumber: 1, pageSize: 500});
      this.staffOptions = records || [];
      if (this.staffOptions.length) {
        this.query.employeeId = this.staffOptions[0].employeeId;
        this.loadReport();
      }
    },
    staffLabel(e) {
      const role = e.roleName ? " · " + e.roleName : "";
      return (e.employeeName || e.employeeId) + role;
    },
    dateOnly(v) {
      return v ? String(v).slice(0, 10) : "—";
    },
    rateColor(r) {
      return r >= 80 ? "#67c23a" : r >= 50 ? "#e6a23c" : "#f56c6c";
    },
    async loadReport() {
      if (!this.query.employeeId) {
        this.report = null;
        return;
      }
      const staff = this.currentStaff;
      // 员工岗位 KPI类型 code → POSITION_ROLE 字典项 id（考核项挂在 dict id 上）
      let kpiPositionId = "";
      if (staff && staff.kpiType) {
        const dictItem = this.kpiTypeDictList.find(t => t.dictCode === staff.kpiType);
        kpiPositionId = dictItem ? dictItem.id : staff.kpiType;
      }
      this.clearFlash();
      this.loading = true;
      try {
        // 并行拉取：单员工报表 + 所选员工当月目标明细
        const [data, goals] = await Promise.all([
          reportApi.report({employeeId: this.query.employeeId, month: this.query.month, kpiPositionId}),
          reportApi.goalRows(this.query.employeeId, this.query.month),
        ]);
        this.report = data || null;
        this.goalRows = goals || [];
      } finally {
        this.loading = false;
      }
    },
    // 点击 未完成/已延期 数字 → 高亮明细中对应行并闪烁
    flashUnfinished(taskGroup, status) {
      let ids;
      if (status === 50) {
        ids = taskGroup.list.filter(x => x.kpiStatus === 50).map(x => x.id);
      } else {
        ids = taskGroup.list.filter(x => x.kpiStatus !== 100 && x.kpiStatus !== 50).map(x => x.id);
      }
      if (!ids.length) {
        return;
      }
      this.clearFlash();
      this.flashIds = ids;
      this.$nextTick(() => {
        if (this.$refs.unfinishTable) {
          this.$refs.unfinishTable.$el.scrollIntoView({behavior: "smooth", block: "center"});
        }
      });
      this.flashTimer = setTimeout(() => {
        this.flashIds = [];
      }, 3000);
    },
    clearFlash() {
      if (this.flashTimer) {
        clearTimeout(this.flashTimer);
        this.flashTimer = null;
      }
      this.flashIds = [];
    },
    rowClass({row}) {
      return this.flashIds.includes(row.id) ? "goal-flash-row" : "";
    },
  },
};
</script>

<style lang="scss" scoped>
.report-list {
  height: 100%;
}

/* list-table 同时是滚动容器，给内部卡片留出内边距 */
:deep(.list-table) {
  padding: 6px 4px;
  box-sizing: border-box;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 14px;

  .stat-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 12px 16px;
    background: #fff;

    .label {
      font-size: 12px;
      color: #909399;
    }

    .value {
      font-size: 24px;
      font-weight: 700;
      height: 34px;
      line-height: 34px;
      margin: 4px 0;

      &.money { font-size: 20px; }
      &.neg { color: #e5484d; }

      .unit {
        font-size: 14px;
        font-weight: 400;
        margin-left: 2px;
      }
    }

    .sub {
      font-size: 12px;
      color: #909399;
    }
  }
}

.tip-bar {
  font-size: 12px;
  color: #909399;
  line-height: 1.7;
  background: #fafbfc;
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 12px;
}

.chart-card {
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: #fff;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 10px;

    .section-sub {
      font-size: 12px;
      font-weight: 400;
      color: #909399;
      margin-left: 8px;
    }
  }
}

.num { font-variant-numeric: tabular-nums; }
.neg { color: #e5484d; }
.bold { font-weight: 600; color: #303133; }
.muted { color: #909399; }
.cell-title { color: #303133; font-weight: 500; }

.clickable-num {
  cursor: pointer;
  text-decoration: underline;

  &:hover { opacity: .8; }
}

:deep(.goal-flash-row) {
  td {
    background: #fdecec !important;
    animation: flash-row 1s ease-in-out infinite;
  }
}

.empty {
  color: #909399;
  padding: 10px;
}

@keyframes flash-row {
  0%, 100% {background-color: #fdecec;}
  50% {background-color: #ffb3b3;}
}
</style>
